import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import DefaultTable from "./DefaultTable";

interface PaginatedTableDetails {
	take: number;
	page: number;
	orderBy: string;
	order: string;
	search?: string;
}

interface PaginatedTableProps {
	defaultOrder: string;
	searchValue?: string;
	getRows: (details: PaginatedTableDetails) => { data?: { rows: any[]; count: number }; isLoading: boolean };
	Skeleton: ReactNode;
	[key: string]: unknown;
}

export default function PaginatedTable(props: PaginatedTableProps) {
	const ref = useRef<{ searchTimeout?: ReturnType<typeof setTimeout> }>({});
	const [details, setDetails] = useState<PaginatedTableDetails>({
		take: 10,
		page: 0,
		orderBy: props.defaultOrder,
		order: "DESC",
		search: props.searchValue,
	});

	const updateDetails = (newDetails: Partial<PaginatedTableDetails>) => {
		setDetails({
			...details,
			...newDetails,
		});
	};

	useEffect(() => {
		clearTimeout(ref.current.searchTimeout);
		ref.current.searchTimeout = setTimeout(() => {
			setDetails((prev) => ({
				...prev,
				search: props.searchValue,
				page: 0,
			}));
		}, 400);
		return () => clearTimeout(ref.current.searchTimeout);
	}, [props.searchValue]);

	const { data: { rows, count } = { rows: [], count: 0 }, isLoading } = props.getRows(details);

	if (isLoading) {
		return props.Skeleton;
	}

	return (
		<DefaultTable
			{...(props as any)}
			{...details}
			rows={rows}
			count={count}
			setDetails={updateDetails}
		/>
	);
}
